"use client";

import { useState } from "react";
import { useMutation } from "convex/react";
import { Check, ThumbsDown, ThumbsUp } from "@/components/icons";
import { api } from "@convex/_generated/api";
import { cn } from "@/lib/utils";
import { handleError } from "@/lib/handle-error";
import { IconButton } from "@/components/ui/icon-button";
import type { GradingAnalysis, GradingFeedbackAccess } from "./grading-session";

type Verdict = "correct" | "incorrect";

interface AnalysisField {
  label: string;
  value: string;
  fieldKey: string;
}

function formatDimensions(
  dimensions: GradingAnalysis["dimensions"],
): string | null {
  if (!dimensions) return null;
  const sides = [dimensions.length, dimensions.width, dimensions.height].filter(
    Boolean,
  );
  return `${sides.join(" × ")} ${dimensions.unit}`;
}

function analysisFields(results: GradingAnalysis): Array<AnalysisField> {
  const dimensions = formatDimensions(results.dimensions);
  const fields: Array<AnalysisField | null> = [
    {
      label: "Product type",
      value: results.productTypeName,
      fieldKey: "productType",
    },
    results.conditionLabel
      ? {
          label: "Condition",
          value: results.conditionLabel,
          fieldKey: "condition",
        }
      : null,
    dimensions
      ? { label: "Dimensions", value: dimensions, fieldKey: "dimensions" }
      : null,
    results.quantity
      ? {
          label: "Est. quantity",
          value: `~${results.quantity.estimated} ${results.quantity.estimated === 1 ? "unit" : "units"}`,
          fieldKey: "quantity",
        }
      : null,
  ];
  return fields.filter((field) => field !== null);
}

export function AnalysisFields({
  results,
  feedbackAccess,
}: {
  results: GradingAnalysis;
  feedbackAccess: GradingFeedbackAccess;
}) {
  return (
    <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
      {analysisFields(results).map((field) => (
        <div key={field.fieldKey} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{field.label}</dt>
          <dd className="flex items-center text-sm font-medium">
            {field.value}
            <FieldFeedback
              feedbackAccess={feedbackAccess}
              fieldKey={field.fieldKey}
            />
          </dd>
        </div>
      ))}
    </dl>
  );
}

function FieldFeedback({
  feedbackAccess,
  fieldKey,
}: {
  feedbackAccess: GradingFeedbackAccess;
  fieldKey: string;
}) {
  const [submitted, setSubmitted] = useState(false);
  const submitFieldFeedback = useMutation(
    api.ai.grading.publicMutations.submitFieldFeedback,
  );

  const submit = async (verdict: Verdict) => {
    try {
      await submitFieldFeedback({ ...feedbackAccess, fieldKey, verdict });
      setSubmitted(true);
    } catch (error) {
      handleError(error);
    }
  };

  if (submitted) {
    return (
      <Check
        aria-label="Feedback sent"
        className="ml-1.5 h-3.5 w-3.5 text-muted-foreground"
      />
    );
  }
  return (
    <span className="ml-1.5 inline-flex items-center gap-0.5">
      <VerdictButton verdict="correct" onSelect={submit} />
      <VerdictButton verdict="incorrect" onSelect={submit} />
    </span>
  );
}

function VerdictButton({
  verdict,
  onSelect,
}: {
  verdict: Verdict;
  onSelect: (verdict: Verdict) => void;
}) {
  const isCorrect = verdict === "correct";
  const label = isCorrect ? "Correct" : "Incorrect";
  return (
    <IconButton
      size="compact"
      aria-label={label}
      title={label}
      onClick={() => onSelect(verdict)}
      className={cn(
        "text-muted-foreground",
        isCorrect
          ? "hover:bg-success-bg hover:text-success-strong"
          : "hover:bg-destructive-bg hover:text-destructive-strong",
      )}
    >
      {isCorrect ? <ThumbsUp /> : <ThumbsDown />}
    </IconButton>
  );
}
